// pages/Avenant.tsx
import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import Sidebar from '../components/Sidebar';
import Navbar from '../components/Navbar';
import NotificationPanel from '../components/NotificationPanel';
import DocumentViewer from '../components/DocumentViewer';
import { useEmployeeDetail } from '../hooks/useEmployeeDetail';
import { getCddDuration, CDD_MAX_MONTHS } from '../utils/employeeStatus';
import { generateDocumentFromTemplate } from '../api/employees';

interface AvenantTemplate {
    filename: string;
    display_name: string;
    relative_path?: string;
    category?: string;
}

interface GeneratedDocument {
    filename: string;
    display_name: string;
    relative_path?: string;
    size: string;
    size_bytes: number;
    modified: string;
    url: string; // relative ou media URL
    absolute_url?: string; // URL complète pour prévisualisation externe
    category?: string;
}

const Avenant: React.FC = () => {
    const { id } = useParams<{ id: string }>();
    const navigate = useNavigate();
    const { employee, loading, error } = useEmployeeDetail(id ? Number(id) : undefined);

    // États
    const [isNotifPanelOpen, setIsNotifPanelOpen] = useState(false);
    const [avenantTemplates, setAvenantTemplates] = useState<AvenantTemplate[]>([]);
    const [generatedDocuments, setGeneratedDocuments] = useState<GeneratedDocument[]>([]);
    const [selectedTemplate, setSelectedTemplate] = useState<string>('');
    const [showModal, setShowModal] = useState(false);
    const [generating, setGenerating] = useState(false);
    const [modalError, setModalError] = useState<string | null>(null);

    // États pour la prévisualisation avec DocumentViewer
    const [previewState, setPreviewState] = useState({
        isOpen: false,
        documentUrl: '',
        documentName: '',
    });
    
    // Champs du modal pour les placeholders
    const [dateEtablissement, setDateEtablissement] = useState(() => {
        const today = new Date();
        return today.toISOString().split('T')[0];
    });
    const [extraFields, setExtraFields] = useState<Record<string, string>>({});

    // Charger les templates & documents générés
    useEffect(() => {
        loadAvenantTemplates();
        if (id) {
            loadGeneratedDocuments();
        }
    }, [id]);

    const loadAvenantTemplates = async () => {
        try {
            const apiUrl = import.meta.env.VITE_API_URL || 'http://192.168.1.3:8000';
            const baseUrl = apiUrl.replace(/\/api\/?$/, '');
            const url = `${baseUrl}/api/employees/employees/list_templates/`;
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access')}`,
                },
            });
            if (!response.ok) {
                console.error('Erreur API templates', response.status);
                return;
            }
            const data = await response.json();
            const templates: AvenantTemplate[] = (data.templates || []).filter((t: any) => {
                const cat = (t.category || '').toLowerCase();
                return cat.includes('avenant') || cat === 'avenants';
            }).map((t: any) => ({
                filename: t.filename,
                display_name: t.display_name,
                relative_path: t.relative_path,
                category: t.category,
            }));
            setAvenantTemplates(templates);
        } catch (err) {
            console.error('Erreur chargement templates:', err);
        }
    };

    const loadGeneratedDocuments = async () => {
        try {
            const apiUrl = import.meta.env.VITE_API_URL || 'http://192.168.1.3:8000';
            // Retirer /api/ de la fin si présent, puis construire l'URL complète
            const baseUrl = apiUrl.replace(/\/api\/?$/, '');
            const url = `${baseUrl}/api/employees/employees/${id}/list_generated_documents/`;
            console.log('🔍 Chargement documents générés depuis:', url);
            
            const response = await fetch(url, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access')}`,
                },
            });
            
            console.log('📡 Réponse API:', response.status);
            
            if (response.ok) {
                const data = await response.json();
                console.log('✅ Documents reçus:', data.documents);

                // Filtrer pour ne garder que les avenants
                const avenants = (data.documents || []).filter((doc: GeneratedDocument) => {
                    const category = (doc.category || '').toLowerCase();
                    return category.includes('avenant') || category === 'avenants';
                });

                console.log('📄 Avenants filtrés:', avenants);
                setGeneratedDocuments(avenants);
            } else {
                console.error('❌ Erreur API:', response.status, await response.text());
            }
        } catch (err) {
            console.error('❌ Erreur chargement documents générés:', err);
        }
    };

    // Déterminer les champs requis selon le template
    const getRequiredFields = (templateFilename: string): Array<{key: string, label: string, type: 'text' | 'date' | 'number'}> => {
        const upper = templateFilename.toUpperCase();
        if (upper.includes('FONCTION')) {
            return [
                { key: 'date_effet', label: 'Date de prise d\'effet', type: 'date' },
                { key: 'num_avenant', label: 'Numéro de l\'avenant', type: 'text' },
                { key: 'new_fonction', label: 'Nouvelle fonction', type: 'text' },
            ];
        } else if (upper.includes('PROROGATION')) {
            return [
                { key: 'num_avenant', label: 'Numéro de l\'avenant', type: 'text' },
                { key: 'debut_date', label: 'Date de début', type: 'date' },
                { key: 'fin_date', label: 'Nouvelle date de fin de contrat (après prorogation)', type: 'date' },
            ];
        } else if (upper.includes('SALAIRE')) {
            return [
                { key: 'date_effet', label: 'Date de prise d\'effet', type: 'date' },
                { key: 'num_avenant', label: 'Numéro de l\'avenant', type: 'text' },
                { key: 'ancien_salaire', label: 'Ancien salaire de base', type: 'number' },
                { key: 'nouveau_salaire', label: 'Nouveau salaire de base', type: 'number' },
                { key: 'prime_transport', label: 'Prime de transport', type: 'number' },
                { key: 'prime_rendement', label: 'Prime de rendement', type: 'number' },
                { key: 'new_sursalaire', label: 'Sur-salaire', type: 'number' },
                { key: 'salaire_catégorie', label: 'Salaire de catégorie', type: 'number' },
            ];
        }
        return [];
    };

    const handleSelectTemplate = () => {
        if (!selectedTemplate) {
            alert('Veuillez sélectionner un template');
            return;
        }
        setExtraFields({});
        setModalError(null);
        setShowModal(true);
    };

    const handleGenerate = async () => {
        try {
            setGenerating(true);
            setModalError(null);

            const requiredFields = getRequiredFields(selectedTemplate);
            
            // Validation des champs requis
            for (const field of requiredFields) {
                if (!extraFields[field.key]?.trim()) {
                    setModalError(`Le champ "${field.label}" est requis.`);
                    setGenerating(false);
                    return;
                }
            }

            // VÉRIFICATION: Si c'est un avenant, vérifier que le numéro n'existe pas déjà
            const numAvenant = extraFields['num_avenant'];
            if (numAvenant) {
                try {
                    const apiUrl = import.meta.env.VITE_API_URL || 'http://192.168.1.3:8000';
                    const baseUrl = apiUrl.replace(/\/api\/?$/, '');
                    const checkUrl = `${baseUrl}/api/employees/employees/${id}/check_avenant_number/?number=${encodeURIComponent(numAvenant)}`;
                    
                    const response = await fetch(checkUrl, {
                        headers: {
                            'Authorization': `Bearer ${localStorage.getItem('access')}`,
                        },
                    });
                    
                    if (!response.ok) {
                        throw new Error('Erreur lors de la vérification du numéro d\'avenant');
                    }
                    
                    const data = await response.json();
                    if (!data.available) {
                        setModalError(data.message || 'Ce numéro d\'avenant existe déjà.');
                        setGenerating(false);
                        return;
                    }
                } catch (checkErr: any) {
                    console.error('Erreur vérification avenant:', checkErr);
                    // Continuer même si la vérification échoue
                }
            }

            // Fonction pour convertir YYYY-MM-DD en JJ/MM/AAAA
            const formatDate = (dateStr: string) => {
                if (!dateStr) return '';
                const [year, month, day] = dateStr.split('-');
                return `${day}/${month}/${year}`;
            };

            // Préparer les extras avec dates formatées
            const extras: Record<string, string> = {
                date_etablissement: formatDate(dateEtablissement),
            };

            // Ajouter les champs extra
            Object.keys(extraFields).forEach(key => {
                const field = requiredFields.find(f => f.key === key);
                if (field?.type === 'date') {
                    extras[key] = formatDate(extraFields[key]);
                } else {
                    extras[key] = extraFields[key];
                }
            });

            const templateToUse = avenantTemplates.find(t => t.filename === selectedTemplate);
            if (!templateToUse) {
                setModalError('Template introuvable');
                setGenerating(false);
                return;
            }

            // Utiliser le chemin relatif si disponible, sinon préfixer par avenants
            const templatePath = templateToUse.relative_path ? templateToUse.relative_path : `avenants/${selectedTemplate}`;
            const blob = await generateDocumentFromTemplate(Number(id), templatePath, { extras });

            // Télécharger le fichier
            const url = URL.createObjectURL(blob);
            const a = document.createElement('a');
            a.href = url;
            a.download = `${templateToUse.display_name}_${employee?.full_name || 'employe'}.docx`;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            URL.revokeObjectURL(url);

            // Recharger la liste des documents générés
            await loadGeneratedDocuments();

            // Fermer le modal
            setShowModal(false);
            setSelectedTemplate('');
            setExtraFields({});
            alert('Document généré avec succès !');
        } catch (err: any) {
            console.error('Erreur génération:', err);
            setModalError(err.message || 'Erreur lors de la génération du document');
        } finally {
            setGenerating(false);
        }
    };

    const handleCloseModal = () => {
        setShowModal(false);
        setModalError(null);
        setExtraFields({});
    };

    const handleDeleteDocument = async (doc: GeneratedDocument) => {
        if (!confirm(`Êtes-vous sûr de vouloir supprimer "${doc.display_name}" ?`)) {
            return;
        }

        try {
            console.log('🗑️ [DELETE] Document:', doc);
            
            const apiUrl = import.meta.env.VITE_API_URL || 'http://192.168.1.3:8000';
            const baseUrl = apiUrl.replace(/\/api\/?$/, '');
            
            const deleteUrl = `${baseUrl}/api/employees/employees/${id}/delete_generated_document/`;
            console.log('🗑️ [DELETE] URL:', deleteUrl);
            console.log('🗑️ [DELETE] Body:', { filename: doc.filename, relative_path: doc.relative_path });
            
            const response = await fetch(deleteUrl, {
                method: 'POST',
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access')}`,
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    filename: doc.filename,
                    relative_path: doc.relative_path
                }),
            });

            console.log('🗑️ [DELETE] Response status:', response.status);

            if (response.ok) {
                alert('Document supprimé avec succès');
                await loadGeneratedDocuments();
                console.log('🗑️ [DELETE] Suppression réussie');
            } else {
                const data = await response.json();
                console.error('🗑️ [DELETE] Error response:', data);
                alert(data.error || 'Erreur lors de la suppression');
            }
        } catch (err) {
            console.error('❌ [DELETE] Erreur suppression:', err);
            alert('Erreur lors de la suppression du document');
        }
    };

    const handlePreviewDocument = async (doc: GeneratedDocument) => {
        try {
            console.log('📄 [PREVIEW] Document:', doc);
            
            // Pour la prévisualisation, utiliser l'endpoint backend download pour récupérer le DOCX
            // puis le prévisualiser avec DocumentViewer
            const apiUrl = import.meta.env.VITE_API_URL || 'http://192.168.1.3:8000/api/';
            const baseUrl = apiUrl.replace(/\/api\/?$/, '');
            
            // Construire l'URL de l'endpoint de téléchargement avec les paramètres
            const relativePath = doc.relative_path || doc.filename;
            console.log('📄 [PREVIEW] Relative path:', relativePath);
            
            const downloadUrl = `${baseUrl}/api/employees/employees/${id}/download_generated_document/?relative_path=${encodeURIComponent(relativePath)}`;
            console.log('📄 [PREVIEW] URL:', downloadUrl);

            // Télécharger le fichier via fetch avec authentification
            const response = await fetch(downloadUrl, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access') || ''}`,
                }
            });

            console.log('📄 [PREVIEW] Response status:', response.status);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('📄 [PREVIEW] Error response:', errorText);
                throw new Error('Impossible de charger le document');
            }

            const blob = await response.blob();
            console.log('📄 [PREVIEW] Blob size:', blob.size);
            
            const blobUrl = URL.createObjectURL(blob);

            // Ouvrir la prévisualisation avec le blob URL
            setPreviewState({
                isOpen: true,
                documentUrl: blobUrl,
                documentName: doc.filename, // Utiliser filename qui contient l'extension
            });
            
            console.log('📄 [PREVIEW] Prévisualisation ouverte');
        } catch (error) {
            console.error('❌ [PREVIEW] Erreur prévisualisation avenant:', error);
            alert('Impossible de prévisualiser le document');
        }
    };

    const closePreview = () => {
        // Nettoyer le blob URL si c'en est un
        if (previewState.documentUrl.startsWith('blob:')) {
            URL.revokeObjectURL(previewState.documentUrl);
        }

        setPreviewState({
            isOpen: false,
            documentUrl: '',
            documentName: '',
        });
    };

    const handleDownloadDocument = async (filename: string, doc: GeneratedDocument) => {
        try {
            console.log('💾 [DOWNLOAD] Document:', doc);
            
            // Utiliser l'endpoint backend qui sert de proxy pour Bunny CDN
            const apiUrl = import.meta.env.VITE_API_URL || 'http://192.168.1.3:8000/api/';
            const baseUrl = apiUrl.replace(/\/api\/?$/, '');
            
            // Construire l'URL de l'endpoint de téléchargement avec les paramètres
            const relativePath = doc.relative_path || doc.filename;
            console.log('💾 [DOWNLOAD] Relative path:', relativePath);
            
            const downloadUrl = `${baseUrl}/api/employees/employees/${id}/download_generated_document/?relative_path=${encodeURIComponent(relativePath)}`;
            console.log('💾 [DOWNLOAD] URL:', downloadUrl);

            // Télécharger le fichier via fetch avec authentification
            const response = await fetch(downloadUrl, {
                headers: {
                    'Authorization': `Bearer ${localStorage.getItem('access') || ''}`,
                }
            });

            console.log('💾 [DOWNLOAD] Response status:', response.status);

            if (!response.ok) {
                const errorText = await response.text();
                console.error('💾 [DOWNLOAD] Error response:', errorText);
                throw new Error('Impossible de télécharger le document');
            }

            const blob = await response.blob();
            console.log('💾 [DOWNLOAD] Blob size:', blob.size);
            
            const blobUrl = URL.createObjectURL(blob);

            // Télécharger via blob URL
            const a = document.createElement('a');
            a.href = blobUrl;
            a.download = filename;
            document.body.appendChild(a);
            a.click();
            document.body.removeChild(a);
            
            // Nettoyer
            URL.revokeObjectURL(blobUrl);
            console.log('💾 [DOWNLOAD] Téléchargement terminé');
        } catch (error) {
            console.error('❌ [DOWNLOAD] Erreur téléchargement avenant:', error);
            alert('Impossible de télécharger le document');
        }
    };

    const handleBack = () => {
        navigate(`/employees/${employee?.id}`);
    };

    if (loading) {
        return (
            <div className={`flex h-screen bg-base-200 ${isNotifPanelOpen ? 'overflow-hidden' : ''}`}>
                <Sidebar />
                <div className="flex-1 flex flex-col p-6 overflow-auto">
                    <Navbar onOpenNotifPanel={() => setIsNotifPanelOpen(true)} />
                    <div className="flex justify-center items-center h-full">
                        <span className="loading loading-spinner loading-lg"></span>
                    </div>
                </div>
                <NotificationPanel 
                    isOpen={isNotifPanelOpen} 
                    onClose={() => setIsNotifPanelOpen(false)} 
                />
            </div>
        );
    }

    if (error || !employee) {
        return (
            <div className={`flex h-screen bg-base-200 ${isNotifPanelOpen ? 'overflow-hidden' : ''}`}>
                <Sidebar />
                <div className="flex-1 flex flex-col p-6 overflow-auto">
                    <Navbar onOpenNotifPanel={() => setIsNotifPanelOpen(true)} />
                    <div className="flex justify-center items-center h-full">
                        <div className="text-center">
                            <h2 className="text-2xl font-bold text-error">
                                {error || 'Employé non trouvé'}
                            </h2>
                            <button 
                                className="btn btn-warning mt-4"
                                onClick={() => navigate('/employees')}
                            >
                                Retour à la liste
                            </button>
                        </div>
                    </div>
                </div>
                
                <NotificationPanel 
                    isOpen={isNotifPanelOpen} 
                    onClose={() => setIsNotifPanelOpen(false)} 
                />
            </div>
        );
    }

    const requiredFields = selectedTemplate ? getRequiredFields(selectedTemplate) : [];

    return (
        <div className={`flex h-screen bg-base-200 ${isNotifPanelOpen ? 'overflow-hidden' : ''}`}>
            <Sidebar />
            
            <div className="flex-1 flex flex-col p-6 overflow-auto">
                <Navbar onOpenNotifPanel={() => setIsNotifPanelOpen(true)} />
                
                <div className="flex flex-col gap-6">
                    {/* En-tête avec bouton retour */}
                    <div className="flex justify-between items-center">
                        <h2 className="text-3xl font-extrabold text-base-content">
                            Avenant - {employee.full_name}
                        </h2>
                        <button 
                            className="btn btn-warning"
                            onClick={handleBack}
                        >
                            ← Retour à la fiche
                        </button>
                    </div>

                    {/* Plafond légal des CDD : 2 ans, renouvellements compris */}
                    {(() => {
                        const cdd = getCddDuration(employee);
                        if (!cdd.isCdd || (!cdd.limitReached && !cdd.approaching)) return null;
                        return (
                            <div className={`alert ${cdd.limitReached ? 'alert-error' : 'alert-warning'} shadow-lg`}>
                                <span className="text-3xl">{cdd.limitReached ? '⛔' : '⚠️'}</span>
                                <div>
                                    <h3 className="font-bold">
                                        {cdd.limitReached
                                            ? `Plafond de ${CDD_MAX_MONTHS} mois atteint — prorogation impossible`
                                            : `Plafond de ${CDD_MAX_MONTHS} mois bientôt atteint`}
                                    </h3>
                                    <div className="text-sm">
                                        Ce CDD totalise <strong>{cdd.months} mois</strong> depuis le{' '}
                                        <strong>{employee.contract_start_date}</strong>
                                        {cdd.limitDate && <> — limite légale au <strong>{cdd.limitDate}</strong></>}.
                                        {cdd.limitReached
                                            ? " Le proroger au-delà exposerait à une requalification en CDI."
                                            : " Vérifiez la durée cumulée avant toute prorogation."}
                                    </div>
                                </div>
                            </div>
                        );
                    })()}

                    {/* Section Génération des Avenants */}
                    <div className="card bg-base-100 shadow-xl">
                        <div className="card-body">
                            <h3 className="text-2xl font-bold text-warning text-center mb-6">
                                📝 Génération des Avenants
                            </h3>
                            
                            <div className="flex flex-col items-center gap-6 p-6">
                                {/* ComboBox pour sélectionner le template */}
                                <div className="w-full max-w-2xl">
                                    <label className="label">
                                        <span className="label-text font-semibold text-lg">Sélectionnez le type d'avenant :</span>
                                    </label>
                                    <select 
                                        className="select select-bordered w-full select-lg"
                                        value={selectedTemplate}
                                        onChange={(e) => setSelectedTemplate(e.target.value)}
                                    >
                                        <option value="">-- Choisir un type d'avenant --</option>
                                        {avenantTemplates.map((template) => (
                                            <option key={template.filename} value={template.filename}>
                                                {template.display_name}
                                            </option>
                                        ))}
                                    </select>
                                </div>

                                {/* Bouton Générer */}
                                <button 
                                    className="btn btn-primary btn-lg"
                                    onClick={handleSelectTemplate}
                                    disabled={!selectedTemplate}
                                >
                                    ✨ Générer l'Avenant
                                </button>
                            </div>
                        </div>
                    </div>

                    {/* Section informative */}
                    <div className="card bg-base-100 shadow-xl">
                        <div className="card-body text-center py-12">
                            <div className="text-6xl mb-4">📋</div>
                            <h3 className="text-lg font-semibold mb-2">
                                Templates d'Avenants Disponibles
                            </h3>
                            <div className="text-left max-w-xl mx-auto mt-4">
                                <ul className="list-disc list-inside space-y-2">
                                    <li><strong>Changement de Fonction</strong> : Pour modifier le poste d'un employé</li>
                                    <li><strong>Prorogation de CDD</strong> : Pour prolonger un contrat à durée déterminée</li>
                                    <li><strong>Modification de Salaire</strong> : Pour ajuster la rémunération</li>
                                </ul>
                            </div>
                        </div>
                    </div>

                    {/* Section Documents Générés */}
                    {generatedDocuments.length > 0 && (
                        <div className="card bg-base-100 shadow-xl">
                            <div className="card-body">
                                <h3 className="text-xl font-bold text-warning mb-4">
                                    📄 Documents Générés ({generatedDocuments.length})
                                </h3>
                                
                                <div className="overflow-x-auto">
                                    <table className="table table-zebra w-full">
                                        <thead>
                                            <tr>
                                                <th>Nom du document</th>
                                                <th>Taille</th>
                                                <th>Date de génération</th>
                                                <th>Actions</th>
                                            </tr>
                                        </thead>
                                        <tbody>
                                            {generatedDocuments.map((doc) => (
                                                <tr key={doc.filename}>
                                                    <td>
                                                        <div className="flex items-center gap-2">
                                                            📝
                                                            <span className="font-medium">{doc.display_name}</span>
                                                        </div>
                                                    </td>
                                                    <td>
                                                        <span className="badge badge-ghost">{doc.size}</span>
                                                    </td>
                                                    <td>{doc.modified}</td>
                                                    <td>
                                                        <div className="flex gap-2">
                                                            <button 
                                                                className="btn btn-info btn-sm"
                                                                onClick={() => handlePreviewDocument(doc)}
                                                                title="Aperçu"
                                                            >
                                                                👁️ Aperçu
                                                            </button>
                                                            <button 
                                                                className="btn btn-success btn-sm"
                                                                onClick={() => handleDownloadDocument(doc.filename, doc)}
                                                                title="Télécharger"
                                                            >
                                                                📥 Télécharger
                                                            </button>
                                                            <button
                                                                className="btn btn-error btn-sm"
                                                                onClick={() => handleDeleteDocument(doc)}
                                                                title="Supprimer"
                                                            >
                                                                🗑️ Supprimer
                                                            </button>
                                                        </div>
                                                    </td>
                                                </tr>
                                            ))}
                                        </tbody>
                                    </table>
                                </div>
                            </div>
                        </div>
                    )}
                </div>
            </div>
            
            {/* Panneau de Notifications */}
            <NotificationPanel 
                isOpen={isNotifPanelOpen} 
                onClose={() => setIsNotifPanelOpen(false)} 
            />

            {/* Modal pour saisir les placeholders */}
            {showModal && (
                <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
                    <div className="bg-base-100 rounded-lg shadow-xl max-w-2xl w-full max-h-[90vh] overflow-y-auto">
                        <div className="p-6">
                            <h3 className="text-2xl font-bold text-warning mb-4">
                                📝 Compléter les informations de l'avenant
                            </h3>

                            {modalError && (
                                <div className="alert alert-error mb-4">
                                    <span>{modalError}</span>
                                </div>
                            )}

                            <div className="space-y-4">
                                {/* Date d'établissement (commune à tous) */}
                                <div>
                                    <label className="label">
                                        <span className="label-text font-semibold">Date d'établissement *</span>
                                    </label>
                                    <input
                                        type="date"
                                        className="input input-bordered w-full"
                                        value={dateEtablissement}
                                        onChange={(e) => setDateEtablissement(e.target.value)}
                                    />
                                </div>

                                {/* Champs spécifiques selon le template */}
                                {requiredFields.map((field) => (
                                    <div key={field.key}>
                                        <label className="label">
                                            <span className="label-text font-semibold">
                                                {field.label} *
                                            </span>
                                        </label>
                                        <input
                                            type={field.type}
                                            className="input input-bordered w-full"
                                            value={extraFields[field.key] || ''}
                                            onChange={(e) => setExtraFields(prev => ({
                                                ...prev,
                                                [field.key]: e.target.value
                                            }))}
                                            placeholder={`Saisir ${field.label.toLowerCase()}`}
                                        />
                                    </div>
                                ))}
                            </div>

                            <div className="flex justify-end gap-4 mt-6">
                                <button 
                                    className="btn btn-outline"
                                    onClick={handleCloseModal}
                                    disabled={generating}
                                >
                                    Annuler
                                </button>
                                <button 
                                    className="btn btn-primary"
                                    onClick={handleGenerate}
                                    disabled={generating}
                                >
                                    {generating ? (
                                        <>
                                            <span className="loading loading-spinner loading-sm"></span>
                                            Génération...
                                        </>
                                    ) : (
                                        <>✨ Générer</>
                                    )}
                                </button>
                            </div>
                        </div>
                    </div>
                </div>
            )}

            {/* Prévisualisation Avenant avec DocumentViewer */}
            {previewState.isOpen && previewState.documentUrl && (
                <DocumentViewer
                    fileUrl={previewState.documentUrl}
                    fileName={previewState.documentName}
                    onClose={closePreview}
                />
            )}
        </div>
    );
};

export default Avenant;